import type { BYOKTier } from '@developerz/domain';
import { createMemo, createSignal, For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { humanTokens } from '../lib/contextWindow';
import { filterModels, MODEL_TAG_MAX, TIER_OPTIONS, tierLabelKey } from '../lib/keyModels';
import type { ProviderModelOption } from '../lib/keys';
import type { ModelTagsController } from '../lib/modelTags';
import { Field } from './Field';
import { Select } from './Select';

// Tag a provider's models to lane tiers (arc 101 slice 02). One select per
// model, all four `BYOKTier` members offered — several models may share a tier,
// which is the whole point of the feature.
//
// FILTERED and SCROLL-BOUNDED, not because a long list is ugly: OpenRouter
// serves ~300 models, and 300 unfiltered selects is a dialog nobody can reach
// the footer of. The filter box swallows Enter (`<ModelChipsField/>`'s rule) —
// it sits inside the capture form, and submitting a key while narrowing a list
// is not what that keystroke means.
//
// The 50-entry ceiling is the SERVER's (`POST /v1/billing/keys`). At the cap the
// UNTAGGED rows' selects shut and the reason is stated; the tagged ones stay
// live, so a reader is never stranded with 50 picks they cannot change.
//
// Each row shows the window the PROVIDER reported, and NOTHING when it reported
// none — no dash, no placeholder, no guess. A reader must be able to tell "the
// provider did not say" from "the provider said 200K", because the number is
// what compaction will run on and a wrong one fails silently and expensively.

export type ModelTierListProps = {
  readonly models: readonly ProviderModelOption[];
  readonly tags: ModelTagsController;
};

/** One DOM id per row: model ids carry `/`, `:` and `.`. The same sanitizer
 *  `ModelPickerList`'s `domId` applies (that one is not exported). */
const tierId = (model: string): string => `model-tier-${model.replace(/[^\w-]/g, '_')}`;

export function ModelTierList(props: ModelTierListProps): JSX.Element {
  const [filter, setFilter] = createSignal('');
  const shown = createMemo(() => filterModels(props.models, filter()));

  const pick = (model: string, value: string): void =>
    props.tags.setTier(model, value === '' ? null : (value as BYOKTier));

  return (
    <Field label={t('account_keys.tier_filter')} id="add-key-model-filter">
      {(a11y) => (
        <>
          <p class="field__desc">
            {t('account_keys.tier_tagged', { count: props.tags.count(), max: MODEL_TAG_MAX })}
          </p>
          <input
            {...a11y}
            class="input"
            value={filter()}
            onInput={(e) => setFilter(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.preventDefault();
            }}
            placeholder={t('account_keys.tier_filter_placeholder')}
          />
          <Show when={props.tags.atCap()}>
            <p class="field__desc">{t('account_keys.tier_cap')}</p>
          </Show>
          <ul class="add-key__tiers">
            <For
              each={shown()}
              fallback={
                <li class="add-key__tier-empty field__desc">
                  {t('account_keys.tier_filter_empty')}
                </li>
              }
            >
              {(option) => (
                <li class="add-key__tier-row">
                  <span class="add-key__tier-name">
                    <span class="add-key__tier-model">{option.id}</span>
                    <Show when={option.context_length}>
                      {(tokens) => (
                        <span class="add-key__tier-context">
                          {t('account_keys.context_window', { size: humanTokens(tokens()) })}
                        </span>
                      )}
                    </Show>
                  </span>
                  <label id={`${tierId(option.id)}-label`} for={tierId(option.id)} class="sr-only">
                    {t('account_keys.tier_for', { model: option.id })}
                  </label>
                  <Select
                    a11y={{ id: tierId(option.id) }}
                    label={t('account_keys.tier_for', { model: option.id })}
                    value={props.tags.tierOf(option.id) ?? ''}
                    options={[
                      { value: '', label: t('account_keys.tier_none') },
                      ...TIER_OPTIONS.map((tier) => ({
                        value: tier,
                        label: t(tierLabelKey(tier)),
                      })),
                    ]}
                    disabled={props.tags.atCap() && props.tags.tierOf(option.id) === undefined}
                    onChange={(value) => pick(option.id, value)}
                  />
                </li>
              )}
            </For>
          </ul>
        </>
      )}
    </Field>
  );
}
